import { Injectable } from '@angular/core';
import {
  NotificationMessageIcon,
  RetryServices,
} from '@app/framework/notification-sidebar/notification-sidebar.component';
import { NotificationsService } from '@services/notifications.service';
import { HttpUtilService } from '@services/http-util.service';
import { catchError } from 'rxjs/operators';
import { Observable, Subject, of } from 'rxjs';
import { TranslationService } from '@services/translation.service';

@Injectable({
  providedIn: 'root',
})
export class SyncService {
  private lastPushRequests = new Map<string, any>();

  constructor(
    private httpService: HttpUtilService,
    private notifications: NotificationsService,
    private translationService: TranslationService
  ) {}

  getLastPushRequest(id, type) {
    return this.lastPushRequests.get(this.getPushRequestKey(id, type));
  }

  private getPushRequestKey(id, type) {
    return `${id}:${type}`;
  }

  private getPushRetryData(
    id,
    type,
    title,
    children,
    syncType,
    integrations,
    syncNewCourse,
    extra
  ) {
    return {
      id,
      type,
      title,
      children,
      syncType,
      integrations,
      syncNewCourse,
      extra,
    };
  }

  push(
    id,
    type,
    title?,
    children?,
    syncType = 'update',
    integrations = [],
    syncNewCourse = false,
    extra?
  ): Observable<any> {
    let respnse: Subject<any> = new Subject();
    const retryData = this.getPushRetryData(
      id,
      type,
      title,
      children,
      syncType,
      integrations,
      syncNewCourse,
      extra
    );
    this.lastPushRequests.set(this.getPushRequestKey(id, type), retryData);
    const text =
      syncType === 'update'
        ? this.translationService.getTranslatedContent('syncing', 'Syncing')
        : this.translationService.getTranslatedContent(
            'restoring',
            'Restoring'
          );

    this.notifications.addUserNotification(
      {
        icon: NotificationMessageIcon.fileExport,
        status: `${this.translationService.getTranslatedContent('in_progress', 'In Progress')}`,
        text: `${text} <b>${title || id}</b>`,
        shouldForceOpen: true,
      },
      id + type
    );
    //query string param pushUpdate triger step function to start on course
    let promise = new Promise(res => {
      let url = `/api/sync/course/${id}/${syncType}`;
      if (children) {
        url += '?children=true';
      }
      // this.syncing = "Started";
      this.httpService
        .request(
          'PUT',
          url,
          { integrations, resetCourse: syncNewCourse, extra },
          null,
          true
        )
        .pipe(catchError(error => of({ error })))
        .subscribe(d => {
          if (d?.error) {
            this.notifications.addUserNotification(
              {
                icon: NotificationMessageIcon.fail,
                status: 'Syncing failed',
                text: `Syncing <b>${title || id}</b> failed`,
                shouldForceOpen: true,
                retry: {
                  service: RetryServices.viewSync,
                  data: retryData,
                },
              },
              id + type
            );
            res(null);
          }
          // console.log(d);
          if (d.executions?.length > 0 && d.executions?.[0]?.executionArn) {
            if (d.status) {
              this.notifications.addUserNotification(
                {
                  icon: NotificationMessageIcon.fileExport,
                  status: `${this.translationService.getTranslatedContent('in_progress', 'In Progress')}`,
                  text: d.status,
                  shouldForceOpen: true,
                },
                id + type
              );
            }
            let disp = d.executions[0];
            let update = setInterval(() => {
              let surl = `/api/sync/course/${id}/${syncType}?executionArn=${disp.executionArn}`;
              this.httpService
                .request('GET', surl, {}, null, true)
                .subscribe(s => {
                  //console.log(s)
                  if (s.status === 'FAILED') {
                    console.log('Failed');
                    console.log(s);
                    clearInterval(update);
                    if (s.error === 'Course not found') {
                      this.notifications.addUserNotification(
                        {
                          icon: NotificationMessageIcon.fail,
                          status: 'Sync New Course',
                          text: `The previously synced course <b>${title || id}</b> may have been deleted in your LMS, Click retry to sync a new version`,
                          shouldForceOpen: true,
                          retry: {
                            service: RetryServices.viewSync,
                            data: { ...retryData, syncNewCourse: true },
                          },
                        },
                        id + type
                      );
                    } else {
                      this.notifications.addUserNotification(
                        {
                          icon: NotificationMessageIcon.fail,
                          status: this.translationService.getTranslatedContent(
                            'syncing_failed',
                            'Syncing failed'
                          ),
                          text: `${this.translationService.getTranslatedContent('syncing', 'Syncing')} <b>${title || id}</b> ${this.translationService.getTranslatedContent('failed', 'Failed')}`,
                          shouldForceOpen: true,
                          retry: {
                            service: RetryServices.viewSync,
                            data: retryData,
                          },
                        },
                        id + type
                      );
                    }
                    res(null);
                  } else if (s.status === 'SUCCEEDED') {
                    console.log('completed');
                    console.log(s);
                    clearInterval(update);
                    this.notifications.addUserNotification(
                      {
                        icon: NotificationMessageIcon.checkmark,
                        status: this.translationService.getTranslatedContent(
                          'completed',
                          'Completed'
                        ),
                        text: `${this.translationService.getTranslatedContent('syncing', 'Syncing')} <b>${title || id}</b> ${this.translationService.getTranslatedContent('completed', 'Completed')}`,
                        shouldForceOpen: false,
                      },
                      id + type
                    );
                    res(null);
                  }
                });
            }, 2000);
          } else if (d.error) {
            this.notifications.addUserNotification(
              {
                icon: NotificationMessageIcon.fail,
                status: this.translationService.getTranslatedContent(
                  'syncing_failed',
                  'Syncing failed'
                ),
                text: `${this.translationService.getTranslatedContent('syncing', 'Syncing')} <b>${title || id}</b> ${this.translationService.getTranslatedContent('failed', 'Failed')}`,
                shouldForceOpen: true,
                retry: {
                  service: RetryServices.viewSync,
                  data: retryData,
                },
              },
              id + type
            );
            res(null);
          } else if (d.status === 'Course undeleted') {
            if (d.urlToCheck) {
              let update = setInterval(() => {
                let surl = `/api/sync/course/${id}/${syncType}?url=${encodeURI(d.urlToCheck)}`;
                this.httpService
                  .request('GET', surl, {}, null, true)
                  .subscribe(s => {
                    //console.log(s)
                    if (s.status === 'failed') {
                      console.log('Failed');
                      console.log(s);
                      clearInterval(update);
                      this.notifications.addUserNotification(
                        {
                          icon: NotificationMessageIcon.fail,
                          status: this.translationService.getTranslatedContent(
                            'restore_failed',
                            '"Restore failed"'
                          ),
                          text: `${this.translationService.getTranslatedContent('restoring', 'Restoring')} <b>${title || id}</b> ${this.translationService.getTranslatedContent('failed', 'Failed')}`,
                          shouldForceOpen: true,
                          retry: {
                            service: RetryServices.viewSync,
                            data: retryData,
                          },
                        },
                        id + type
                      );
                      res(null);
                    } else if (s.status === 'completed') {
                      console.log('completed');
                      console.log(s);
                      clearInterval(update);
                      this.notifications.addUserNotification(
                        {
                          icon: NotificationMessageIcon.checkmark,
                          status: this.translationService.getTranslatedContent(
                            'completed',
                            'Completed'
                          ),
                          text: `${this.translationService.getTranslatedContent('restoring', 'Restoring')} <b>${title || id}</b> ${this.translationService.getTranslatedContent('completed', 'Completed')}`,
                          shouldForceOpen: false,
                        },
                        id + type
                      );
                      res(null);
                    }
                  });
              }, 2000);
            }
          } else {
            if (!d.status && d.executions?.length === 0) {
              d.status = this.translationService.getTranslatedContent(
                'no_integration_found',
                'No Integration Found'
              );
            }
            this.notifications.addUserNotification(
              {
                icon: NotificationMessageIcon.fail,
                status: this.translationService.getTranslatedContent(
                  'syncing_failed',
                  'Syncing failed'
                ),
                text: d.status,
                shouldForceOpen: true,
                retry: {
                  service: RetryServices.viewSync,
                  data: retryData,
                },
              },
              id + type
            );
            res(null);
          }
        });
    });
    promise.then(d => {
      respnse.next(d);
      respnse.complete();
    });
    return respnse as Observable<any>;
  }
}
